  import { useState, useEffect } from 'react';
  import { 
    collection, 
    getDocs, 
    doc, 
    updateDoc, 
    query, 
    orderBy,
    getDoc,
    setDoc,
    arrayUnion,
    serverTimestamp,
    where,
    deleteDoc
  } from 'firebase/firestore';
  import { getFunctions, httpsCallable } from 'firebase/functions';
  import { db } from '../config/firebase';
  import { useAuth } from '../hooks/useAuth';
  import { logAdminActivity } from '../services/adminActivityLog';
  import { useToast } from '../components/ui/Toast';
  import Card, { CardHeader, CardBody } from '../components/ui/Card';
  import Button from '../components/ui/Button';
  import Badge from '../components/ui/Badge';
  import { Input } from '../components/ui/FormField';
  import UserDetailsModal from '../components/UserDetailsModal';
  import {
    Search,
    Ban,
    CheckCircle,
    MapPin,
    Phone,
    Mail,
    Calendar,
    Eye,
    UserX,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    Trash2,
    Download
  } from 'lucide-react';

  const Customers = () => {
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [loadingAction, setLoadingAction] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const customersPerPage = 10;
    const { toast } = useToast();
    const { user: currentAdmin, isMasterAdmin } = useAuth();

    useEffect(() => {
      fetchCustomers();
    }, []);

    useEffect(() => {
      filterCustomers();
    }, [customers, searchTerm, filterStatus]);

    // Reset to page 1 when filters change
    useEffect(() => {
      setCurrentPage(1);
    }, [searchTerm, filterStatus]);

    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const customersRef = collection(db, 'users');
        const q = query(customersRef, orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        
        const customersData = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(user => user.type === 'customer');
        
        setCustomers(customersData);
      } catch (error) {
        console.error('Error fetching customers:', error);
        toast.error('Failed to fetch customers');
      } finally {
        setLoading(false);
      }
    };

    const filterCustomers = () => {
      let filtered = [...customers];

      // Filter by search term
      if (searchTerm) {
        filtered = filtered.filter(customer => 
          customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.phoneNumber?.includes(searchTerm) ||
          customer.uid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.id?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Filter by status
      if (filterStatus !== 'all') {
        filtered = filtered.filter(customer => {
          if (filterStatus === 'banned') {
            return customer.status === 'banned';
          } else if (filterStatus === 'active') {
            return !customer.status || customer.status === 'active';
          }
          return true;
        });
      }

      setFilteredCustomers(filtered);
    };

    // Pagination calculations
    const totalPages = Math.ceil(filteredCustomers.length / customersPerPage);
    const startIndex = (currentPage - 1) * customersPerPage;
    const endIndex = startIndex + customersPerPage;
    const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

    // Pagination handlers
    const goToPage = (page) => {
      setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    // Export the CURRENT filter view to a CSV that Excel opens as a
    // spreadsheet. Uses a UTF-8 BOM so Arabic names render correctly in
    // Excel on Windows.
    //
    // Customers don't have a `total_orders_delivered` field maintained
    // on their user doc (only drivers do — that field is incremented by
    // the incrementOrdersDelivered Cloud Function). To fill the Total
    // Orders column we fetch every customer-side order once during
    // export and count per customerId in memory. One extra roundtrip,
    // acceptable for an on-demand admin action.
    const exportToCSV = async () => {
      if (filteredCustomers.length === 0) {
        toast.error('No customers to export');
        return;
      }
      let orderCounts = {};
      try {
        const orderSnap = await getDocs(collection(db, 'orders'));
        orderSnap.forEach((doc) => {
          const cid = doc.data().customerId;
          if (!cid) return;
          orderCounts[cid] = (orderCounts[cid] || 0) + 1;
        });
      } catch (e) {
        console.warn('Failed to load order counts, exporting with zeros', e);
      }

      const headers = ['Name', 'Phone', 'Email', 'Status', 'Total Orders', 'Rating', 'Created At'];
      const rows = filteredCustomers.map((c) => [
        c.name || '',
        c.phoneNumber || '',
        c.email || '',
        c.status || '',
        orderCounts[c.id] || 0,
        typeof c.rating === 'number' ? c.rating.toFixed(2) : '',
        c.createdAt?.seconds
          ? new Date(c.createdAt.seconds * 1000).toLocaleString()
          : '',
      ]);
      const escape = (cell) => `"${String(cell).replace(/"/g, '""')}"`;
      const csv = [
        headers.map(escape).join(','),
        ...rows.map((r) => r.map(escape).join(',')),
      ].join('\n');
      const bom = '﻿';
      const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `customers_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Exported ${filteredCustomers.length} customers`);
    };

    const banCustomer = async (customer) => {
      if (!window.confirm(`Are you sure you want to ban ${customer.name}? This will block their phone number.`)) {
        return;
      }

      try {
        setLoadingAction(true);
        
        const customerRef = doc(db, 'users', customer.id);
        await updateDoc(customerRef, {
          status: 'banned',
          bannedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        try {
          const blockedNumberId = `${customer.phoneNumber}-${Date.now()}`;
          const blockedNumberRef = doc(db, 'BlockedNumbers', blockedNumberId);
          
          await setDoc(blockedNumberRef, {
            number: customer.phoneNumber,
            owner: {
              id: customer.id,
              name: customer.name,
              type: 'customer'
            },
            blockedAt: serverTimestamp(),
            blockedBy: isMasterAdmin ? 'admin_master' : currentAdmin.name,
            blockedByUid: currentAdmin.uid,
            blockedByType: currentAdmin.type,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (blockError) {
          console.warn('Failed to add to BlockedNumbers:', blockError);
        }

        await fetchCustomers();
        logAdminActivity({
          actor: currentAdmin, action: 'ban_customer',
          targetType: 'customer', targetId: customer.id || customer.uid,
          targetLabel: customer.name,
        });
        toast.success(`${customer.name} has been banned and phone number blocked`);
      } catch (error) {
        console.error('Error banning customer:', error);
        toast.error(`Failed to ban customer: ${error.message}`);
        await fetchCustomers();
      } finally {
        setLoadingAction(false);
      }
    };

    const unbanCustomer = async (customer) => {
      if (!window.confirm(`Are you sure you want to unban ${customer.name}?`)) {
        return;
      }

      try {
        setLoadingAction(true);
        
        const customerRef = doc(db, 'users', customer.id);
        await updateDoc(customerRef, {
          status: 'active',
          bannedAt: null,
          unbannedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        try {
          const blockedNumbersRef = collection(db, 'BlockedNumbers');
          const q = query(blockedNumbersRef, where('number', '==', customer.phoneNumber));
          const snapshot = await getDocs(q);
          
          const deletePromises = snapshot.docs.map(docSnapshot => 
            deleteDoc(doc(db, 'BlockedNumbers', docSnapshot.id))
          );
          await Promise.all(deletePromises);
        } catch (blockError) {
          console.warn('Failed to remove from BlockedNumbers:', blockError);
        }

        await fetchCustomers();
        logAdminActivity({
          actor: currentAdmin, action: 'unban_customer',
          targetType: 'customer', targetId: customer.id || customer.uid,
          targetLabel: customer.name,
        });
        toast.success(`${customer.name} has been unbanned and phone number unblocked`);
      } catch (error) {
        console.error('Error unbanning customer:', error);
        toast.error(`Failed to unban customer: ${error.message}`);
        await fetchCustomers();
      } finally {
        setLoadingAction(false);
      }
    };

    // admin_master-only HARD delete: removes the customer from Firebase Auth
    // AND deletes the Firestore user document + subcollections via the
    // `adminDeleteUser` Cloud Function.
    const hardDeleteCustomer = async (customer) => {
      if (!isMasterAdmin) return;
      const confirm1 = window.confirm(
        `⚠️ Permanently delete ${customer.name} (${customer.phoneNumber})?\n\n` +
        `This removes the customer from Firebase Auth AND Firestore (including ` +
        `addresses, ratings). This cannot be undone.`
      );
      if (!confirm1) return;
      const typed = window.prompt(`Type DELETE to confirm deletion of ${customer.name}:`);
      if (typed !== 'DELETE') return;

      try {
        setLoadingAction(true);
        const fns = getFunctions();
        const adminDeleteUser = httpsCallable(fns, 'adminDeleteUser');
        await adminDeleteUser({ userId: customer.id });
        logAdminActivity({
          actor: currentAdmin, action: 'delete_customer',
          targetType: 'customer', targetId: customer.id || customer.uid,
          targetLabel: customer.name,
        });
        toast.success(`${customer.name} has been permanently deleted`);
        await fetchCustomers();
      } catch (error) {
        console.error('Error deleting customer:', error);
        toast.error(`Failed to delete customer: ${error.message}`);
      } finally {
        setLoadingAction(false);
      }
    };

    const viewAddresses = async (customer) => {
      setSelectedCustomer(customer);
      setShowAddressModal(true);
      setLoadingAction(true);

      try {
        const addressesRef = collection(db, 'users', customer.id, 'addresses');
        const snapshot = await getDocs(addressesRef);

        const addressesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setAddresses(addressesData);
      } catch (error) {
        console.error('Error fetching addresses:', error);
        setAddresses([]);
        toast.error('Failed to load addresses');
      } finally {
        setLoadingAction(false);
      }
    };

    const viewDetails = (customer) => {
      setSelectedCustomer(customer);
      // Preload addresses so the Addresses tab opens instantly when first selected.
      (async () => {
        try {
          const snap = await getDocs(collection(db, 'users', customer.id, 'addresses'));
          setAddresses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch {
          setAddresses([]);
        }
      })();
      setShowDetailsModal(true);
    };

    const formatDate = (timestamp) => {
      if (!timestamp) return 'N/A';
      
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else {
        return 'N/A';
      }
      
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    };

    const getStatusBadge = (customer) => {
      const isBanned = customer.status === 'banned';
      return (
        <Badge variant={isBanned ? 'error' : 'success'}>
          {isBanned ? 'Banned' : 'Active'}
        </Badge>
      );
    };

    if (loading) {
      return (
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-600 mt-1">Manage and monitor customer accounts</p>
        </div>

        {/* Filters and Search */}
        <Card>
          <CardBody>
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search by name, email, phone, or UID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="flex gap-2">
                <Button
                  variant={filterStatus === 'all' ? 'primary' : 'outline'}
                  onClick={() => setFilterStatus('all')}
                >
                  All ({customers.length})
                </Button>
                <Button
                  variant={filterStatus === 'active' ? 'primary' : 'outline'}
                  onClick={() => setFilterStatus('active')}
                >
                  Active ({customers.filter(c => !c.status || c.status === 'active').length})
                </Button>
                <Button
                  variant={filterStatus === 'banned' ? 'primary' : 'outline'}
                  onClick={() => setFilterStatus('banned')}
                >
                  Banned ({customers.filter(c => c.status === 'banned').length})
                </Button>
              </div>

              {/* Export to Excel (CSV with UTF-8 BOM) */}
              <Button
                variant="outline"
                onClick={exportToCSV}
                disabled={loading || filteredCustomers.length === 0}
                title="Export the current filter view to a CSV file (opens in Excel)"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>

              {/* Refresh Button */}
              <Button
                variant="outline"
                onClick={fetchCustomers}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Customers Table */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold">
              Customer List ({filteredCustomers.length})
            </h2>
          </CardHeader>
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentCustomers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        No customers found
                      </td>
                    </tr>
                  ) : (
                    currentCustomers.map((customer) => (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {customer.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              UID: {customer.uid || customer.id}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center text-sm text-gray-900">
                              <Mail className="w-4 h-4 mr-2 text-gray-400" />
                              {customer.email}
                            </div>
                            <div className="flex items-center text-sm text-gray-500">
                              <Phone className="w-4 h-4 mr-2 text-gray-400" />
                              {customer.phoneNumber}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(customer)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-2" />
                            {formatDate(customer.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => viewDetails(customer)}
                              disabled={loadingAction}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Details
                            </Button>
                            {customer.status === 'banned' ? (
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => unbanCustomer(customer)}
                                disabled={loadingAction}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Unban
                              </Button>
                            ) : (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => banCustomer(customer)}
                                disabled={loadingAction}
                              >
                                <Ban className="w-4 h-4 mr-1" />
                                Ban
                              </Button>
                            )}
                            {isMasterAdmin && (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => hardDeleteCustomer(customer)}
                                disabled={loadingAction}
                                title="Permanently delete from Auth + Firestore"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(endIndex, filteredCustomers.length)}</span> of{' '}
                    <span className="font-medium">{filteredCustomers.length}</span> customers
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => goToPage(page)}
                              className={`min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                currentPage === page
                                  ? 'bg-primary-600 text-white'
                                  : 'border border-gray-300 hover:bg-white text-gray-700'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return (
                            <span key={page} className="px-2 text-gray-400">
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>
                    
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Address Modal */}
        {showAddressModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      Customer Addresses
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedCustomer?.name} - {addresses.length} address{addresses.length !== 1 ? 'es' : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowAddressModal(false);
                      setSelectedCustomer(null);
                      setAddresses([]);
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {loadingAction ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="text-gray-500 mt-4">Loading addresses...</p>
                  </div>
                ) : addresses.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="font-medium">No addresses found</p>
                    <p className="text-sm mt-1">This customer hasn't added any delivery addresses yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {addresses.map((address, index) => (
                      <div
                        key={address.id}
                        className="border border-gray-200 rounded-xl p-4 hover:border-primary-300 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-primary-600" />
                            <h4 className="font-semibold text-gray-900">
                              {address.title || `Address ${index + 1}`}
                            </h4>
                          </div>
                          {address.isDefault && (
                            <Badge variant="primary">Default</Badge>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Name:</span>
                            <span className="ml-2 text-gray-900 font-medium">{address.name}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Phone:</span>
                            <span className="ml-2 text-gray-900 font-medium">{address.phoneNumber}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">City:</span>
                            <span className="ml-2 text-gray-900 font-medium">{address.city}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Area:</span>
                            <span className="ml-2 text-gray-900 font-medium">{address.area}</span>
                          </div>
                          {(address.lat || address.lng) && (
                            <div className="md:col-span-2">
                              <span className="text-gray-500">Coordinates:</span>
                              <span className="ml-2 text-gray-900 font-medium">
                                {typeof address.lat === 'number' ? address.lat.toFixed(6) : 'N/A'}, {typeof address.lng === 'number' ? address.lng.toFixed(6) : 'N/A'}
                              </span>
                            </div>
                          )}
                          {address.createdAt && (
                            <div className="md:col-span-2">
                              <span className="text-gray-500">Added:</span>
                              <span className="ml-2 text-gray-900">{formatDate(address.createdAt)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showDetailsModal && selectedCustomer && (
          <UserDetailsModal
            user={selectedCustomer}
            onClose={() => setShowDetailsModal(false)}
            addressesPreloaded={addresses}
          />
        )}
      </div>
    );
  };

  export default Customers;